import {
  Fragment,
  useState,
} from 'react';
import {
  Callout,
  Chip,
  Empty,
  Loading,
  Panel,
  TableScroll,
  formatCount,
  formatDate,
  useLoad,
  countryLabel,
  rowOpen,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../../client.ts';
import {
  affiliateCommission,
  affiliateStatusLabel,
  affiliateStatusTone,
} from '../../../screens.ts';
import { t } from '../../../messages.ts';
import { Failure } from '../../shared.tsx';
import { AffiliateDetailModal } from '../../affiliates/AffiliateDetailModal.tsx';

/**
 * §3.9a / §3.9e / E30 — the operator_id-scoped screen the decision named.
 * Loaded on an explicit click, not on mount: §3.9c logs **per access**, and
 * a panel that fetched on every page open would log reads nobody actually
 * asked for. §3.9b — disclosed, not assumed — is why the note from the API
 * response is shown here rather than only written to the audit trail.
 */
export function OperatorAffiliatesPanel({ api, operatorId, shown, onShow }: {
  api: PlatformApi;
  operatorId: string;
  shown: boolean;
  onShow: () => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  // The `shown` guard belongs in the loader, not only in the render below:
  // a hook runs on mount whatever the render then returns, so this fetched —
  // and §3.9c logged the cross-tenant access — before anyone clicked the
  // button that exists to ask for it.
  const loaded = useLoad(
    () => (shown ? api.operatorAffiliates(operatorId) : Promise.resolve(null)),
    [operatorId, shown],
  );

  if (!shown) {
    return (
      <Panel title={t('ops.affs.title')}>
        <Callout>
          {t('ops.affs.note')}
        </Callout>
        <div className="form-actions">
          <button className="primary" onClick={onShow}>
            {t('ops.affs.view')}
          </button>
        </div>
      </Panel>
    );
  }

  if (loaded.state === 'error') return <Panel title={t('ops.affs.title')}><Failure error={loaded.error} /></Panel>;
  // `value` is null for exactly one tick — the render after `shown` flips,
  // before the loader this re-ran has resolved. Same skeleton as loading.
  if (loaded.state === 'loading' || !loaded.value) {
    return <Panel title={t('ops.affs.title')}><Loading rows={2} /></Panel>;
  }

  return (
    <Panel title={t('ops.affs.titleFor', { operator: loaded.value.operator })} aside={<span className="count count-badge">{loaded.value.items.length}</span>}>
      {open ? <AffiliateDetailModal api={api} affiliateId={open} onClose={() => setOpen(null)} /> : null}
      {loaded.value.items.length === 0 ? (
        <Empty>{t('ops.affs.none')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <th>{t('common.name')}</th><th>{t('common.email')}</th><th>{t('common.country')}</th><th>{t('common.status')}</th>
                <th>{t('ops.list.brands')}</th><th className="num">{t('ops.affs.clicks')}</th><th className="num">{t('ops.affs.bot')}</th>
                <th className="num">{t('ops.affs.players')}</th><th className="num">{t('ops.affs.commission')}</th>
                <th className="num">{t('ops.list.joined')}</th>
              </tr>
            </thead>
            <tbody>
              {/* Batch 2 item 8 — Status showed `shown_to_affiliate` (e.g.
                  "Under Review"), so a SUSPENDED affiliate read as merely
                  pending. The chip is the real status now; what the
                  affiliate is told moves to a row note when it differs,
                  spanning the ten columns above (Name, Email, Country,
                  Status, Brands, Clicks, Bot, Players, Commission, Joined). */}
              {loaded.value.items.map((a) => {
                const openThis = () => setOpen(a.id);
                // Batch 2 review, finding 5 — comparing "UNDER REVIEW"
                // (the portal word) with "UNDER_REVIEW" (the ledger code)
                // by case alone still leaves the space and the underscore
                // apart, so this flagged every state as "shown differently"
                // even when it was not. Strip both before comparing.
                const normalize = (s: string) => s.toUpperCase().replace(/[\s_]+/g, '');
                const seesDifferently = normalize(a.shown_to_affiliate) !== normalize(a.status);
                return (
                  <Fragment key={a.id}>
                    <tr
                      className={seesDifferently ? 'row-clickable has-note' : 'row-clickable'}
                      onClick={openThis}
                      {...rowOpen(openThis)}
                    >
                      <td>{a.legal_name}</td>
                      <td className="faint">{a.email}</td>
                      <td>{countryLabel(a.country)}</td>
                      <td>
                        {/* Batch 2 review, finding 5 — the raw ledger code in
                            the chip, and every non-ACTIVE state read as
                            `overdue` (a failure) including PENDING and
                            UNDER_REVIEW, which are a review, not a verdict
                            (§6.3a). A word, and the DESIGN §4 tone table. */}
                        <Chip tone={affiliateStatusTone(a.status)}>{affiliateStatusLabel(a.status)}</Chip>
                        {a.is_dormant ? <Chip tone="neutral">{t('ops.affs.dormant')}</Chip> : null}
                        {a.purged ? <Chip tone="overdue">{t('ent.state.purged')}</Chip>
                          : a.deleted ? <Chip tone="neutral">{t('ent.state.deleted')}</Chip> : null}
                        {a.pending_reason ? <div className="faint">{a.pending_reason}</div> : null}
                      </td>
                      <td>
                        {a.brands.length === 0 ? (
                          <span className="faint">—</span>
                        ) : (
                          a.brands.map((b) => <Chip key={b.brand_id} tone="neutral">{b.brand}</Chip>)
                        )}
                      </td>
                      <td className="num">{formatCount(a.clicks)}</td>
                      <td className="num faint">{formatCount(a.bot_clicks)}</td>
                      <td className="num">{formatCount(a.players)}</td>
                      <td className="num money">{affiliateCommission(a)}</td>
                      <td className="num faint">{formatDate(a.joined)}</td>
                    </tr>
                    {seesDifferently ? (
                      <tr className="row-note row-clickable" onClick={openThis}>
                        <td colSpan={10}>
                          <span className="row-note-text">{t('ops.affs.theySee', { text: a.shown_to_affiliate })}</span>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
      )}
      <Callout>{loaded.value.note}</Callout>
    </Panel>
  );
}
